/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.variantContainer = this.querySelector('.sticky-atc__size');
        this.variantTitle = this.querySelector('.sticky-atc__size-value');
        this.sizeSelect = this.querySelector('.sticky-atc__size-select');
        this.sizeOptionIndex = Number(this.variantContainer?.dataset.optionIndex ?? -1);
        if (this.variantContainer && !this.section.querySelector('variant-picker')) {
          this.variantContainer.hidden = true;
        }

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.sizeSelect?.addEventListener('change', this.onSizeChange.bind(this));
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          if (this.sizeSelect) {
            this.sizeSelect.value = evt.detail.variant.options?.[this.sizeOptionIndex] || '';
          }
          if (this.variantTitle) {
            this.variantTitle.textContent = this.sizeOptionIndex >= 0
              ? evt.detail.variant.options?.[this.sizeOptionIndex] || evt.detail.variant.title
              : evt.detail.variant.title;
          }
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          if (this.variantTitle) this.variantTitle.textContent = this.variantContainer.dataset.selectText;
          if (idInput) idInput.value = '';
        }

        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Use the main picker as the single source of variant selection.
       */
      onSizeChange() {
        const picker = this.section.querySelector('variant-picker');
        const selector = picker?.querySelectorAll('.option-selector:not(.option-selector--custom)')[this.sizeOptionIndex];
        const radio = Array.from(selector?.querySelectorAll('input.js-option') || [])
          .find(input => input.value === this.sizeSelect.value);
        if (radio) {
          radio.click();
        } else {
          const target = this.section.querySelector('#product-info');
          if (target) theme.scrollToRevealElement(target);
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
        this.submitBtn.disabled = !variantAvailable;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
